import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useState } from "react";
import CustomSelect from "../../shared/CustomSelect";
import CustomArea from "../../shared/CustomArea";
import CustomInput from "../../shared/CustomInput";
import { Switch } from "antd";
import AddProductSuccess from "../../components/add-product/AddProductSuccess";
import { useMutation } from "react-query";
import { addLoanProduct } from "../../sdk/products/products";
import { useError } from "../../context/ErrorProvider";
import Loading from "../../shared/Loading";
import useAuth from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";

const CreateLoanProduct = () => {
  const [productForm, setProductForm] = useState({
    // Product Details
    productType: "",
    productName: "",
    productDescription: "",

    // Product Terms
    interestRate: "",
    interestType: "",
    minimumLoanAmount: "",
    maximumLoanAmount: "",
    minimumDuration: "",
    maximumDuration: "",

    // Product Repayment Terms
    repaymentFrequency: "",
    gracePeriod: "",
    penaltyRate: "",

    // Eligibility & Conditions
    minimumAge: "",
    collateralRequired: false,

    // Fees
    processingFeeType: "",
    processingFee: "",
    insuranceFee: "",
    otherCharges: "",

    // Terms & Conditions
    termsAndConditions: "",
    requiredDocuments: "",
  });
  const [openAddProductSuccess, setOpenAddProductSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const { showError } = useError();
  const { auth } = useAuth();
  const navigate = useNavigate();

  const toggleAddProductSuccess = () => {
    setOpenAddProductSuccess(true);
  };
  const closeAddProductSuccess = () => {
    setOpenAddProductSuccess(false);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!productForm.productType)
      newErrors.productType = "Product type is required.";
    if (!productForm.productName)
      newErrors.productName = "Product name is required.";
    if (!productForm.productDescription)
      newErrors.productDescription = "Description is required.";
    if (!productForm.interestRate)
      newErrors.interestRate = "Interest rate is required.";
    if (!productForm.interestType)
      newErrors.interestType = "Interest type is required.";
    if (!productForm.minimumLoanAmount)
      newErrors.minimumLoanAmount = "Minimum loan amount is required.";
    if (!productForm.maximumLoanAmount)
      newErrors.maximumLoanAmount = "Maximum loan amount is required.";
    if (!productForm.minimumDuration)
      newErrors.minimumDuration = "Minimum duration is required.";
    if (!productForm.maximumDuration)
      newErrors.maximumDuration = "Maximum duration is required.";
    if (!productForm.repaymentFrequency)
      newErrors.repaymentFrequency = "Repayment frequency is required.";
    if (!productForm.gracePeriod)
      newErrors.gracePeriod = "Grace period is required.";
    if (!productForm.minimumAge)
      newErrors.minimumAge = "Minimum age is required.";
    if (!productForm.processingFee)
      newErrors.processingFee = "Processing fee is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateProduct = () => {
    if (validateForm()) {
      mutate();
    }
  };

  const { mutate, isLoading } = useMutation({
    mutationFn: () =>
      addLoanProduct(
        productForm.productName,
        productForm.productDescription,
        Number(productForm.interestRate),
        Number(productForm.minimumDuration),
        Number(productForm.maximumDuration),
        Number(productForm.maximumLoanAmount),
        Number(productForm.minimumLoanAmount),
        productForm.interestType,
        productForm.repaymentFrequency,
        Number(productForm.gracePeriod),
        auth?.user?.id,
        productForm?.processingFee,
        productForm?.insuranceFee,
        productForm.penaltyRate,
        productForm.requiredDocuments,
        productForm?.termsAndConditions,
        productForm?.otherCharges,
        productForm?.productType,
        productForm?.processingFeeType,
      ),
    onSuccess: () => {
      toggleAddProductSuccess();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  return (
    <>
      {openAddProductSuccess && (
        <AddProductSuccess closeAddProductSuccess={closeAddProductSuccess} />
      )}
      <div className="pl-[10px] w-full">
        <div className="flex items-center h-[40px] justify-between">
          <div className="flex items-center gap-[5px]">
            <ChevronLeft color="#000000" size={17} />
            <span
              onClick={() => navigate("/dashboard/loan-products")}
              className="cursor-pointer"
            >
              Products
            </span>
            <ChevronRight color="#000000" size={17} />
            <span className="text-[16px] text-[#222222]">Create a product</span>
          </div>
        </div>
        <p className="text-[20px] mb-[15px] text-[#333333] font-bold">
          Create Loan Products
        </p>
        <div>
          <p className="text-[#000000] text-[16px] font-semibold">
            Product Details
          </p>
          <div className="flex items-start justify-between mb-[20px]">
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="product Type"
              >
                Product Type
              </label>
              <CustomSelect
                name="gender"
                value={productForm.productType}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    productType: e.target.value,
                  })
                }
                options={[
                  "Individual/Personal",
                  "Salaried",
                  "Development",
                  "Business",
                ]}
              />
              {errors.productType && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.productType}
                </span>
              )}
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="product name"
              >
                Product Name
              </label>
              <CustomInput
                placeholder="Enter your product name"
                type="text"
                value={productForm.productName}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    productName: e.target.value,
                  })
                }
              />
              {errors.productName && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.productName}
                </span>
              )}
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="product description"
              >
                Product Description
              </label>
              <CustomArea
                placeholder="Enter your product description"
                type="text"
                value={productForm.productDescription}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    productDescription: e.target.value,
                  })
                }
              />
              {errors.productDescription && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.productDescription}
                </span>
              )}
            </div>
          </div>
        </div>
        <div>
          <p className="text-[#000000] text-[16px] font-semibold">
            Product Terms
          </p>
          <div className="flex items-start justify-between mb-[20px]">
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="Interest Rate (%)"
              >
                Interest Rate Per Month (%)
              </label>
              <CustomInput
                placeholder="Enter interest rate"
                type="text"
                value={productForm.interestRate}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    interestRate: e.target.value,
                  })
                }
              />
              {errors.interestRate && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.interestRate}
                </span>
              )}
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="interest Type"
              >
                Interest Type
              </label>
              <CustomSelect
                name="interest type"
                value={productForm.interestType}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    interestType: e.target.value,
                  })
                }
                options={["standard", "reducing"]}
              />
              {errors.interestType && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.interestType}
                </span>
              )}
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="minimum Loan Amount (KES)"
              >
                Minimum Loan Amount (KES)
              </label>
              <CustomInput
                placeholder="Enter amount"
                type="text"
                value={productForm.minimumLoanAmount}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    minimumLoanAmount: e.target.value,
                  })
                }
              />
              {errors.minimumLoanAmount && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.minimumLoanAmount}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-start justify-between my-[20px]">
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="Maximum Loan Amount (KES)"
              >
                Maximum Loan Amount (KES)
              </label>
              <CustomInput
                placeholder="Enter amount"
                type="text"
                value={productForm.maximumLoanAmount}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    maximumLoanAmount: e.target.value,
                  })
                }
              />
              {errors.maximumLoanAmount && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.maximumLoanAmount}
                </span>
              )}
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="minimum Duration (Months)"
              >
                Minimum Duration (Months)
              </label>
              <CustomInput
                placeholder="Enter duration (months)"
                type="text"
                value={productForm.minimumDuration}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    minimumDuration: e.target.value,
                  })
                }
              />
              {errors.minimumDuration && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.minimumDuration}
                </span>
              )}
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="maximum Duration (Months)"
              >
                Maximum Duration (Months)
              </label>
              <CustomInput
                placeholder="Enter duration (months)"
                type="text"
                value={productForm.maximumDuration}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    maximumDuration: e.target.value,
                  })
                }
              />
              {errors.maximumDuration && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.maximumDuration}
                </span>
              )}
            </div>
          </div>
        </div>
        <div>
          <p className="text-[#000000] text-[16px] font-semibold">
            Product Repayment Terms
          </p>
          <div className="flex items-start justify-between mb-[20px]">
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="Repayment frequency"
              >
                Repayment Frequency
              </label>
              <CustomSelect
                name="Repayment frequency"
                value={productForm.repaymentFrequency}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    repaymentFrequency: e.target.value,
                  })
                }
                options={["weekly", "monthly"]}
              />
              {errors.repaymentFrequency && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.repaymentFrequency}
                </span>
              )}
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="grace Period (Months)"
              >
                Grace Period (Months)
              </label>
              <CustomSelect
                name="grace Period (Months)"
                value={productForm.gracePeriod}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    gracePeriod: e.target.value,
                  })
                }
                options={["0", "1", "2"]}
              />
              {errors.gracePeriod && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.gracePeriod}
                </span>
              )}
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="penalty rate (% per late installment)"
              >
                Penalty Rate (% per late installment)
              </label>
              <CustomInput
                placeholder="Enter rate"
                type="text"
                value={productForm.penaltyRate}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    penaltyRate: e.target.value,
                  })
                }
              />
              {errors.penaltyRate && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.penaltyRate}
                </span>
              )}
            </div>
          </div>
        </div>
        <div>
          <p className="text-[#000000] text-[16px] font-semibold">
            Eligibility & Conditions
          </p>
          <div className="flex items-start gap-[2%] mb-[20px]">
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="Minimum age of borrower"
              >
                Minimum Age of Borrower
              </label>
              <CustomInput
                placeholder="Enter minimum age"
                type="text"
                value={productForm.minimumAge}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    minimumAge: e.target.value,
                  })
                }
              />
              {errors.minimumAge && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.minimumAge}
                </span>
              )}
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="Collateral required"
              >
                Collateral Required
              </label>
              <div className="w-fit">
                <Switch
                  onChange={(value) => {
                    setProductForm({
                      ...productForm,
                      collateralRequired: value,
                    });
                  }}
                />
                {errors.collateralRequired && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.collateralRequired}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div>
          <p className="text-[#000000] text-[16px] font-semibold">Fees</p>
          <div className="flex items-start justify-between mb-[20px]">
            <div className="flex w-[24%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="Processing fee type"
              >
                Processing Fee Type
              </label>
              <CustomSelect
                placeholder="Enter type"
                type="text"
                value={productForm.processingFeeType}
                options={["percentage", "amount"]}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    processingFeeType: e.target.value,
                  })
                }
              />

              {errors.processingFeeType && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.processingFeeType}
                </span>
              )}
            </div>
            <div className="flex w-[24%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="Processing fee (%)"
              >
                Processing Fee (Kshs.)
              </label>
              <CustomInput
                placeholder="Enter rate"
                type="text"
                value={productForm.processingFee}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    processingFee: e.target.value,
                  })
                }
              />
              {errors.processingFee && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.processingFee}
                </span>
              )}
            </div>
            <div className="flex w-[24%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="insuarance fee (%)"
              >
                Insuarance Fee (%)
              </label>
              <CustomInput
                placeholder="Enter rate (%)"
                type="text"
                value={productForm.insuranceFee}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    insuranceFee: e.target.value,
                  })
                }
              />
              {errors.insuranceFee && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.insuranceFee}
                </span>
              )}
            </div>
            <div className="flex w-[24%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="other Charges"
              >
                Other Charges
              </label>
              <CustomInput
                placeholder="Enter charges (%)"
                type="text"
                value={productForm.otherCharges}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    otherCharges: e.target.value,
                  })
                }
              />
              {errors.otherCharges && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.otherCharges}
                </span>
              )}
            </div>
          </div>
        </div>
        <div>
          <p className="text-[#000000] text-[16px] font-semibold">
            Terms & Conditions
          </p>
          <div className="flex items-start gap-[2%] mb-[20px]">
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="Terms & Conditions"
              >
                Terms & Conditions
              </label>
              <CustomArea
                placeholder="Enter terms"
                type="text"
                value={productForm.termsAndConditions}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    termsAndConditions: e.target.value,
                  })
                }
              />
              {errors.termsAndConditions && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.termsAndConditions}
                </span>
              )}
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="document required (List)"
              >
                Document Required (List)
              </label>
              <CustomArea
                placeholder="Enter documents"
                type="text"
                value={productForm.requiredDocuments}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    requiredDocuments: e.target.value,
                  })
                }
              />
              {errors.requiredDocuments && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.requiredDocuments}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end mb-[20px] gap-[10px]">
          <button
            disabled={isLoading}
            onClick={handleCreateProduct}
            className="bg-[#000000] h-[35px] rounded-[30px] text-[#ffffff] text-[14px] font-normal px-[20px] flex items-center justify-center gap-[5px]"
          >
            {isLoading ? <Loading /> : <Plus size={16} color="#ffffff" />}
            Create product
          </button>
        </div>
      </div>
    </>
  );
};

export default CreateLoanProduct;
